/**
 * Klientská česká lokalizace DealerHub Spare Parts Finder web-componenty.
 *
 * Widget neumí cs/cs-CZ (Zod enum: de|en|es-ES|fr|it|pt|sv-SE) a nemá runtime hook
 * pro vlastní messages — i18n je server-side bundlovaný (react-intl) a překlady
 * se fetchují z https://sparepartsfinderwebcomponent.dealerhub.com/intl/v1/.
 *
 * Workaround: widget startuje s locale='en', tady přepisujeme rendrovaný text
 * v jeho shadow rootu (mode:"open") přes MutationObserver. Slovník je extrahován
 * z runtime bundlu — viz docs/spare-parts-finder-widget/translations/{en,cs}.json.
 *
 * Tradeoffs (viz docs/spare-parts-finder-widget/README.md):
 *   - Stringový match, ne msg-id → nové verze widgetu s novým UI textem nepokryjeme
 *     (vrátí se anglicky). Údržba slovníku je nutná.
 *   - Krátký flash anglického textu (50–200 ms) než MutationObserver doběhne.
 *   - Datumy/čísla zůstanou v `en-US` formátu (widget interně bere date-fns enUS).
 *
 * Použití (default vypnuté): v index.ts odkomentovat `import * as SparePartsCzech`
 * a v DOMContentLoaded `SparePartsCzech.initialize();`. Pak `npm run build-prod`.
 *
 * ClickUp #86c9w3x13.
 */

/** Slovník en → cs. Klíčem je doslovný anglický string z widget bundlu. */
const DICT: Record<string, string> = {
    "Overview": "Přehled",
    "Change model": "Změnit model",
    "PowerParts": "PowerParts",
    "Restart": "Restart",
    "Spare Part Kits": "Sady náhradních dílů",
    "perfect fit for this model:": "ideální pro tento model:",
    "Show model": "Zobrazit model",
    "Article Name": "Název dílu",
    "Article Number": "Číslo dílu",
    "Please enter the article number or the article name to see which vehicles your spare part is compatible with.":
        "Zadejte číslo dílu nebo jeho název a zjistěte, s kterými modely je váš náhradní díl kompatibilní.",
    "or": "nebo",
    "Reset all fields": "Vymazat všechna pole",
    "Search": "Hledat",
    "Compatibility check": "Ověření kompatibility",
    "No Image": "Žádný obrázek",
    "Search ...": "Hledat...",
    "Search...": "Hledat...",
    "Engine": "Motor",
    "Frame": "Rám",
    "WP Parts": "WP díly",
    "Bicycle Accessories": "Cyklo příslušenství",
    "Bicycle General": "Cyklo obecné",
    "Bicycle Parts": "Cyklo díly",
    "Chassis": "Podvozek",
    "Fork": "Vidlice",
    "Shock": "Tlumič",
    "Steering Damper": "Tlumič řízení",
    "or select your brand": "nebo vyberte značku",
    "or select your category": "nebo vyberte kategorii",
    "Check compatibility": "Ověřit kompatibilitu",
    "Search for spare parts or article numbers to see which vehicles they are compatible with.":
        "Hledejte náhradní díly nebo čísla dílů a zjistěte, s kterými vozidly jsou kompatibilní.",
    "Spare parts": "Náhradní díly",
    "Your recently viewed models": "Naposledy zobrazené modely",
    "Select your model and find fitting spare parts.": "Vyberte svůj model a najděte odpovídající náhradní díly.",
    "OEM part": "OEM díl",
    "No result. Please try again.": "Žádný výsledek. Zkuste to prosím znovu.",
    "You can search for example: Super Duke, 1290, 530 EXC, etc.":
        "Můžete zadat například: Super Duke, 1290, 530 EXC apod.",
    "You can search for example: 700 MT, 650NK, etc.":
        "Můžete zadat například: 700 MT, 650NK apod.",
    "You can search for example: EC 300 GP, EC 350F, etc.":
        "Můžete zadat například: EC 300 GP, EC 350F apod.",
    "You can search for example: Svartpilen 801, FE 501, etc.":
        "Můžete zadat například: Svartpilen 801, FE 501 apod.",
    "You can search for example: Brutale 100 RR 2024, 800 NK SPORT, etc.":
        "Můžete zadat například: Brutale 100 RR 2024, 800 NK SPORT apod.",
    "You can search for example: X-Bow GT-XR; X-Bow GT2, etc.":
        "Můžete zadat například: X-Bow GT-XR; X-Bow GT2 apod.",
    "You can search for example: APEX PRO 6500 Cartridge, etc.":
        "Můžete zadat například: APEX PRO 6500 Cartridge apod.",
    "You can find the VIN of your vehicle in the vehicle registration document or on the type plate on your vehicle. The type plate can be mounted at various positions on your vehicle depending on type and model.":
        "VIN vašeho vozidla najdete v technickém průkazu nebo na typovém štítku na vozidle. Typový štítek bývá v různých místech podle typu a modelu vozidla.",
    "Where do I find my VIN?": "Kde najdu VIN?",
    "Type VIN or model name": "Zadejte VIN nebo název modelu",
    "search for your model": "vyhledat váš model",
    "Your result": "Váš výsledek",
    "An unknown error occurred": "Došlo k neznámé chybě",
    "Available": "Skladem",
    "Available at manufacturer": "Dostupné u výrobce",
    "Not available": "Není skladem",
    "Unknown": "Neznámé",
    // Krátká varianta záměrně: sloupec s cenou má ve widgetu napevno 400 px
    // (`minWidth: p ? 450 : 400`) a delší popisek se v něm tísnil.
    "Add to cart": "Do košíku",
    "Added": "Přidáno",
    "PDF Download": "Stáhnout PDF",
    "PDF loading failed": "Načtení PDF selhalo",
    "Exploded view": "Rozkladový pohled",
    "List": "Seznam",
    "Illustration": "Ilustrace",
    "Article / Art. No.": "Díl / č. dílu",
    "No": "Č.",
    "Price": "Cena",
    "Stock status": "Skladovost",
    "Send request": "Odeslat poptávku",
    "SPARE PARTS FINDER": "VYHLEDÁVAČ NÁHRADNÍCH DÍLŮ",
    "Article & art. no.": "Díl a č. dílu",
    "Component sub group": "Podskupina dílů",
    "Model Name": "Název modelu",
    "Model variant": "Varianta modelu",
    "Model Year": "Modelový rok",
    "Apply": "Použít",
    "Component Group": "Skupina dílů",
    "Model Variant": "Varianta modelu",
    "All": "Vše",
    "Select ...": "Vyberte...",
    "Select year": "Vyberte rok",
    "Previous step": "Předchozí krok",
    "Model selector": "Výběr modelu",
    "Back": "Zpět",
    "Remove all": "Odebrat vše",
    "You can add your desired items to the wish list here to request them from your dealer or to print out a list of them.":
        "Sem si můžete přidat položky, které chcete poptat u svého prodejce, nebo si je vytisknout.",
    "There are no items on the wish list yet.": "Seznam přání je zatím prázdný.",
    "Model": "Model",
    "Article": "Díl",
    "Article No.": "Č. dílu",
    "Availability": "Dostupnost",
    "Qty": "Ks",
    "Wishlist": "Seznam přání",
    "Dev Environment": "Vývojové prostředí",
    "Staging Environment": "Staging prostředí",
    "STG Environment": "STG prostředí",
    "Test Environment": "Testovací prostředí",
    "TST Environment": "TST prostředí",
    "Loading...": "Načítá se...",
    "Loading": "Načítá se",
    "No Options Found": "Nenalezeny žádné možnosti",
    "Copied to clipboard": "Zkopírováno do schránky",
    "Add filter": "Přidat filtr",
    "Clear filters": "Zrušit filtry",
    "There are no matching entries for your selection.": "Vašemu výběru neodpovídají žádné záznamy.",
    "Please check your filters and keywords.": "Zkontrolujte filtry a hledaná slova.",
    "No data found": "Nic nenalezeno",
    "Items per page:": "Položek na stránku:",
    "Filter": "Filtr",
    "mm/dd/yyyy": "dd.mm.rrrr",
    "Filter by": "Filtrovat podle",
    "Reset Filters": "Zrušit filtry",
    "Try again": "Zkusit znovu",
    "In case this issue continues, contact support": "Pokud problém přetrvává, kontaktujte podporu",
    "A communication error occurred": "Došlo k chybě komunikace",
    "There was a problem loading the requested data.": "Při načítání požadovaných dat došlo k problému.",
    "This might be temporary. Please try again or try reloading the whole page":
        "Může to být dočasné. Zkuste to prosím znovu nebo načtěte stránku.",
    "Unable to fetch data": "Nelze načíst data",
    "There are no entries for your selection": "Pro váš výběr nejsou k dispozici žádné záznamy",
    "Danger": "Chyba",
    "Information": "Informace",
    "Success": "Úspěch",
    "Warning": "Upozornění",
    "Failure": "Selhání",
    "no value set": "hodnota nenastavena",
    "no date set": "datum nenastaveno",
};

/**
 * ICU plurály řešíme jako regex. Widget v en použije "X option" / "X options" —
 * matchneme číslo + slovo a vrátíme českou plurálovou formu.
 *
 * Pravidla CZ: 1 = "volba", 2-4 = "volby", 5+ a 0 = "voleb".
 */
const PLURAL_RULES: Array<{ pattern: RegExp; replace: (count: number) => string }> = [
    {
        pattern: /\b(\d+)\s+option(s)?\b/g,
        replace: (count) => {
            if (count === 1) return `${count} volba`;
            if (count >= 2 && count <= 4) return `${count} volby`;
            return `${count} voleb`;
        },
    },
];

/** HTML atributy s uživatelsky viditelným textem. */
const TRANSLATABLE_ATTRS = ["placeholder", "title", "aria-label", "alt"];

/**
 * Ceny widget formátuje podle locale 'en' (`CZK 12,095.00`) a runtime override
 * nemá — stejně jako u textů (čeština v jeho Zod enumu chybí). Přepisujeme je
 * proto touž cestou na `12 095 Kč`: pevná mezera jako oddělovač tisíců,
 * haléře jen když nejsou nulové.
 */
const PRICE_PATTERN = /^CZK\s+(\d{1,3}(?:,\d{3})*)\.(\d{2})$/;

function translatePrice(trimmed: string): string | null {
    const match = trimmed.match(PRICE_PATTERN);

    if (match === null) {
        return null;
    }

    const whole = match[1].replace(/,/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    return match[2] === "00" ? `${whole} Kč` : `${whole},${match[2]} Kč`;
}

/** Zachová původní leading/trailing whitespace, ať se neporozbíjí inline layout. */
function keepPadding(original: string, replacement: string): string {
    const leading = original.match(/^\s*/)?.[0] ?? "";
    const trailing = original.match(/\s*$/)?.[0] ?? "";

    return `${leading}${replacement}${trailing}`;
}

function translateString(s: string): string {
    const trimmed = s.trim();
    if (trimmed === "") return s;

    const direct = DICT[trimmed];
    if (direct !== undefined) {
        return keepPadding(s, direct);
    }

    const price = translatePrice(trimmed);
    if (price !== null) {
        return keepPadding(s, price);
    }

    for (const rule of PLURAL_RULES) {
        if (rule.pattern.test(trimmed)) {
            return s.replace(rule.pattern, (_match, num) => rule.replace(parseInt(num, 10)));
        }
    }

    return s;
}

function translateTextNodesIn(root: Node): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
        const text = (node as Text).data;
        const next = translateString(text);
        if (next !== text) {
            (node as Text).data = next;
        }
    }
}

function translateAttributesIn(root: Element): void {
    const elements = root.querySelectorAll("[" + TRANSLATABLE_ATTRS.join("], [") + "]");
    for (const el of Array.from(elements)) {
        for (const attr of TRANSLATABLE_ATTRS) {
            const val = el.getAttribute(attr);
            if (val !== null) {
                const next = translateString(val);
                if (next !== val) el.setAttribute(attr, next);
            }
        }
    }
}

function translateSubtree(root: Node): void {
    translateTextNodesIn(root);
    if (root instanceof Element) {
        translateAttributesIn(root);
    }
}

function attachObserver(shadowRoot: ShadowRoot): void {
    // Inicialní průchod celého shadow rootu.
    translateSubtree(shadowRoot);

    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type === "characterData" && m.target.nodeType === Node.TEXT_NODE) {
                const text = (m.target as Text).data;
                const next = translateString(text);
                if (next !== text) (m.target as Text).data = next;
            } else if (m.type === "childList") {
                m.addedNodes.forEach(translateSubtree);
            } else if (m.type === "attributes") {
                const el = m.target as Element;
                const attr = m.attributeName;
                if (attr && TRANSLATABLE_ATTRS.includes(attr)) {
                    const val = el.getAttribute(attr);
                    if (val !== null) {
                        const next = translateString(val);
                        if (next !== val) el.setAttribute(attr, next);
                    }
                }
            }
        }
    });

    observer.observe(shadowRoot, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: TRANSLATABLE_ATTRS,
    });
}

let attachedRoot: ShadowRoot | null = null;

function attachOnce(root: ShadowRoot): void {
    if (attachedRoot === root) {
        return;
    }

    attachedRoot = root;
    attachObserver(root);
}

/**
 * Najdi web-component a po jeho zaregistrování + attachShadow napojí observer.
 * Polling, protože `customElements.whenDefined()` slibuje, ale shadow root vzniká
 * až v `connectedCallback` po `renderApp()` — `whenDefined` nestačí, potřebujeme
 * fakt `el.shadowRoot != null`. Polluju každých 50 ms max 60× (=3 s).
 *
 * Samotný polling ale nestačí na detailu motorky: element je v DOM od začátku,
 * jenže 5,7MB skript widgetu se injektuje až na `shown.bs.tab`, takže shadow root
 * do tří sekund zpravidla nevznikne a celá lokalizace se vzdá. Proto navíc
 * posloucháme `spfOnWebComponentLoaded`, který widget vypálí na `document`
 * po namountování — stejně jako to dělá spareParts.ts.
 */
export function initialize(): void {
    const widget = document.querySelector("pmag-spare-parts-finder");

    if (widget === null) {
        // Stránka bez widgetu — nic neděláme.
        return;
    }

    document.addEventListener("spfOnWebComponentLoaded", () => {
        const root = (widget as Element).shadowRoot;

        if (root !== null) {
            attachOnce(root);
        }
    });

    let tries = 0;
    const max = 60;
    const tick = () => {
        const root = (widget as Element).shadowRoot;
        if (root !== null) {
            attachOnce(root);
            return;
        }
        if (++tries >= max) {
            // Na detailu motorky je to očekávané — dokud se tab neotevře, widget
            // se nenačte. Doběhne pak přes spfOnWebComponentLoaded výš.
            return;
        }
        setTimeout(tick, 50);
    };
    tick();
}
